import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FaTriangleExclamation } from "react-icons/fa6";
import ModalBasic from "@/components/ModalBasic";
import Loader from "@/components/Loader";
import { DangerButton } from "@/components/Buttons";
import { addToast } from "@/store/app/action";
import { getStore } from "@/api/settings";
import { disconnectClover } from "@/api/clover";
import { getSupportedPOSProviders, deletePOSCredentials } from "@/api/posCredentials";
import { getSftpConfig } from "@/api/sftp";

import ProviderTiles, { SftpTile } from "./ProviderTiles";
import CredentialsForm from "./connect/CredentialsForm";
import OAuthLinkPanel from "./connect/OAuthLinkPanel";
import SftpSettings from "./connect/SftpSettings";
import CloverConnectContent from "./connect/Clover";
import SquareConnectContent from "./connect/Square";
import ToastConnectContent from "./connect/Toast";
import LightspeedConnectContent from "./connect/Lightspeed";
import Settings from "./settings";
import { providerPresentation, usesOAuth } from "./providerCatalog";

// v2 integrations are hardcoded on purpose. Each one is a bespoke OAuth
// handshake implemented in this dashboard, so there is no server-side catalogue
// to drive them and nothing to discover — and keeping them out of the fetch
// means a catalogue outage can't take Clover down with it.
const V2_PROVIDERS = [
  { provider: "CloverV2", name: "Clover", provide_oauth_link: true },
];

const V2_KEYS = new Set(V2_PROVIDERS.map((p) => p.provider.toLowerCase()));

// The OAuth handshakes this dashboard implements.
const OAUTH_COMPONENTS = {
  CloverV2: CloverConnectContent,
  square: SquareConnectContent,
  toast: ToastConnectContent,
  lightspeed: LightspeedConnectContent,
};

const SectionHeading = ({ title, caption }) => (
  <div className="mb-3">
    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
      {title}
    </h3>
    {caption && (
      <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{caption}</p>
    )}
  </div>
);

const POSConnection = ({ onPosConnectionChange }) => {
  const dispatch = useDispatch();
  const { selectedLocation } = useSelector((state) => state.app);
  const storeId = selectedLocation?.store_id;

  const [fullLocationDetail, setFullLocationDetail] = useState(null);
  const [connection, setConnection] = useState({});
  const [v1Providers, setV1Providers] = useState([]);
  const [catalogError, setCatalogError] = useState(null);
  const [selected, setSelected] = useState(null); // descriptor | 'sftp'
  const [initializing, setInitializing] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [sftpSettings, setSftpSettings] = useState(null);

  const refreshLocationDetails = useCallback(async () => {
    try {
      const res = await getStore();
      setFullLocationDetail(res.store);
      setConnection(res?.store?.api_credentials || {});
      if (typeof onPosConnectionChange === "function") {
        onPosConnectionChange(!!res?.store?.api_credentials?.provider);
      }
    } catch (error) {
      console.error("Failed to fetch location details:", error);
    }
  }, [onPosConnectionChange]);

  const refreshSftp = useCallback(async () => {
    if (!storeId) return;
    try {
      const res = await getSftpConfig(storeId);
      const payload = res && typeof res === 'object' && !Array.isArray(res) ? res : null;
      setSftpSettings(payload?.import_settings ?? payload);
    } catch (error) {
      // A store with no settings, or a server without the route, is simply "not
      // configured" here — the modal reports the difference.
      setSftpSettings(null);
    }
  }, [storeId]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setInitializing(true);
      await Promise.all([refreshLocationDetails(), refreshSftp()]);

      try {
        const list = await getSupportedPOSProviders();
        if (cancelled) return;
        // Drop anything the catalogue reports that we already serve as a v2
        // integration, so a provider never appears in both sections.
        setV1Providers(
          (Array.isArray(list) ? list : [])
            .filter((p) => p?.provider && !V2_KEYS.has(String(p.provider).toLowerCase()))
            // Catalogue order is whatever Mongo returns; alphabetical keeps the
            // grid stable as providers are added. `numeric` so "POS 360" sorts
            // against "POS 9" by value rather than by digit.
            .sort((a, b) =>
              String(a.provider).localeCompare(String(b.provider), undefined, {
                sensitivity: 'base',
                numeric: true,
              })
            )
        );
        setCatalogError(null);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load supported POS providers:", error);
        setV1Providers([]);
        setCatalogError(
          error?.response?.data?.message ||
            "Could not load the list of supported v1 integrations."
        );
      } finally {
        if (!cancelled) setInitializing(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [selectedLocation, refreshLocationDetails, refreshSftp]);

  const sftpHost = sftpSettings?.credentials?.host || null;
  const sftpConnected = !!sftpHost;

  const refreshAll = useCallback(async () => {
    await Promise.all([refreshLocationDetails(), refreshSftp()]);
  }, [refreshLocationDetails, refreshSftp]);

  const connectedKey = connection?.provider || null;
  // `is_active` is what the inventory pullers filter on
  // (`<Provider>Credential.find({ master: false, is_active: true })`), so a
  // deactivated credential still exists but nothing syncs against it. Absent
  // means active, matching the schema default.
  const integrationActive = connection?.is_active !== false;
  const connectedDescriptor = useMemo(
    () => [...V2_PROVIDERS, ...v1Providers].find((p) => p.provider === connectedKey) || null,
    [v1Providers, connectedKey]
  );
  const connectedPresentation = providerPresentation(connectedDescriptor || connectedKey);

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      // CloverV2 holds an OAuth grant that has to be revoked provider-side; the
      // credential row alone is not the whole connection.
      if (connectedKey === "CloverV2") {
        await disconnectClover();
      } else if (storeId) {
        await deletePOSCredentials(storeId);
      }
      setConnection({});
      await refreshLocationDetails();
      dispatch(
        addToast({
          type: "success",
          message: `Disconnected from ${connectedPresentation.name}`,
          timer: 5,
        })
      );
    } catch (error) {
      console.error("Failed to disconnect:", error);
      dispatch(
        addToast({
          type: "error",
          message:
            error?.response?.data?.message || "Failed to disconnect this POS connection",
          timer: 5,
        })
      );
    } finally {
      setDisconnecting(false);
    }
  };

  const closeModal = () => setSelected(null);
  const isSftp = selected === "sftp";
  const descriptor = isSftp ? null : selected;
  const OAuthComponent =
    descriptor && usesOAuth(descriptor) ? OAUTH_COMPONENTS[descriptor.provider] : null;

  const modalTitle = isSftp
    ? "SFTP Upload"
    : descriptor
      ? `Connect to ${providerPresentation(descriptor).name}`
      : "";

  if (initializing) {
    return (
      <div className="flex flex-col items-center justify-center py-10">
        <Loader />
        <p className="mt-3 text-slate-600 dark:text-slate-300">
          Checking your POS connection...
        </p>
      </div>
    );
  }

  const modal = (
    <ModalBasic
      width="3xl"
      modalOpen={!!selected}
      setModalOpen={closeModal}
      title={modalTitle}
    >
      {isSftp ? (
        <SftpSettings
          storeId={storeId}
          onClose={closeModal}
          onSaved={refreshAll}
        />
      ) : OAuthComponent ? (
        <OAuthComponent
          onClose={closeModal}
          isAlreadyConnected={connectedKey === descriptor?.provider}
          onConnectionSuccess={refreshLocationDetails}
        />
      ) : descriptor && usesOAuth(descriptor) ? (
        <OAuthLinkPanel
          descriptor={descriptor}
          storeId={storeId}
          onClose={closeModal}
          onConnectionSuccess={refreshLocationDetails}
        />
      ) : descriptor ? (
        <CredentialsForm
          descriptor={descriptor}
          storeId={storeId}
          saved={connectedKey === descriptor.provider ? connection : null}
          onClose={closeModal}
          onConnectionSuccess={refreshLocationDetails}
        />
      ) : null}
    </ModalBasic>
  );

  if (!connectedKey) {
    return (
      <div>
        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
          POS Connection
        </h2>
        <p className="mt-1 mb-8 text-slate-600 dark:text-slate-300">
          Choose how products come into your external feed.
        </p>

        <section className="mb-8">
          <SectionHeading
            title="v2 integrations"
            caption="Authorize directly with the provider — no API keys to copy."
          />
          <ProviderTiles
            providers={V2_PROVIDERS}
            connectedProvider={connectedKey}
            onSelect={setSelected}
          />
        </section>

        <section className="mb-8">
          <SectionHeading
            title="v1 integrations"
            caption="Connect with API details issued by your POS provider."
          />
          {catalogError ? (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
              <FaTriangleExclamation className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
              <p className="text-sm text-amber-800 dark:text-amber-200">{catalogError}</p>
            </div>
          ) : v1Providers.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No v1 integrations are available in this environment.
            </p>
          ) : (
            <ProviderTiles
              providers={v1Providers}
              connectedProvider={connectedKey}
              onSelect={setSelected}
            />
          )}
        </section>

        <section>
          <SectionHeading title="File feeds" />
          <SftpTile
            onClick={() => setSelected("sftp")}
            connected={sftpConnected}
            host={sftpHost}
          />
        </section>

        {modal}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">
          POS Connection
        </h2>
        <p className="text-slate-600 dark:text-slate-300 mt-1">
          Connected to <span className="font-semibold">{connectedPresentation.name}</span>
        </p>
      </div>

      <div
        className={`border rounded-lg p-4 mb-6 ${
          integrationActive
            ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
            : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
        }`}
      >
        <div className="flex items-center gap-3 flex-wrap">
          {connectedPresentation.logo && (
            <img
              src={connectedPresentation.logo}
              alt=""
              aria-hidden="true"
              className={`h-8 w-8 object-contain ${integrationActive ? '' : 'grayscale opacity-60'}`}
            />
          )}
          <div className="min-w-0">
            {integrationActive ? (
              <>
                <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                  Connection active
                </h3>
                <p className="mt-0.5 text-sm text-emerald-700 dark:text-emerald-300">
                  Your POS system is connected and syncing.
                </p>
              </>
            ) : (
              <>
                <h3 className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-800 dark:text-amber-200">
                  <FaTriangleExclamation className="w-3.5 h-3.5" />
                  Connected, but not syncing
                </h3>
                <p className="mt-0.5 text-sm text-amber-700 dark:text-amber-300">
                  This integration is deactivated, so inventory jobs skip it. The
                  connection details are still here &mdash; reactivating is done by Saucey.
                </p>
              </>
            )}
          </div>

          <div className="ml-auto flex items-center gap-2">
            {connectedDescriptor && !usesOAuth(connectedDescriptor) && (
              <button
                onClick={() => setSelected(connectedDescriptor)}
                className="px-3 py-1.5 text-sm font-medium text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-white dark:hover:bg-slate-700"
              >
                Edit details
              </button>
            )}
            <DangerButton onClick={handleDisconnect} loading={disconnecting}>
              Disconnect
            </DangerButton>
          </div>
        </div>
      </div>

      <Settings
        onDisconnect={() => {
          setConnection({});
          refreshLocationDetails();
        }}
        fullLocationDetail={fullLocationDetail}
      />

      {/* A store can run a POS integration and an SFTP feed at once, so this
          stays reachable rather than only appearing on the picker. */}
      <section className="mt-8">
        <SectionHeading title="File feeds" />
        <SftpTile
          onClick={() => setSelected("sftp")}
          connected={sftpConnected}
          host={sftpHost}
        />
      </section>

      {modal}
    </div>
  );
};

export default POSConnection;
